import { observer } from 'mobx-react-lite'
import { Switch, SxProps, Theme, Typography } from '@mui/material'
import { useState, useEffect } from 'react'
import { useLocalizationStore } from '../../localization/store/localization.store'
import { useCookieConsentStore, CookieConsent } from '../store/cookie-consent.store'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { AppDivider } from 'src/component/common/analog/molecule/AppDivider'

interface CookieSettingsModalProps {
  open: boolean
  onClose: () => void
}

export const CookieSettingsModal = observer(({ open, onClose }: CookieSettingsModalProps) => {
  const cookieConsentStore = useCookieConsentStore()
  const { translation } = useLocalizationStore()
  const [localConsent, setLocalConsent] = useState<CookieConsent>(cookieConsentStore.consent)

  useEffect(() => {
    if (open) {
      setLocalConsent({ ...cookieConsentStore.consent })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleToggle = (key: keyof CookieConsent): void => {
    if (key === 'necessary') {
      return
    }
    setLocalConsent({
      ...localConsent,
      [key]: !localConsent[key],
    })
  }

  const handleSave = (): void => {
    cookieConsentStore.setCustomConsent(localConsent)
    onClose()
  }

  const handleAcceptAll = (): void => {
    cookieConsentStore.acceptAll()
    onClose()
  }

  const texts = translation.cookieConsent.settings

  return (
    <ModalWindow
      modalProps={{
        open,
        onClose,
        PaperProps: {
          sx: {
            maxWidth: { xs: '90%', md: '70%', lg: '40%' },
            borderRadius: '10px',
          },
        },
      }}
      titleProps={{
        textValue: texts.title(),
      }}
      subtitleProps={{
        textValue: texts.description(),
        sx: { color: 'text.secondary' },
      }}
    >
      <AppBox sx={styles.container}>
        <AppBox sx={styles.categoryBox}>
          <AppBox sx={styles.categoryHeader}>
            <Typography sx={styles.categoryTitle}>{texts.necessary.title()}</Typography>
            <AppBox sx={styles.switchContainer}>
              <Typography sx={styles.alwaysActive}>{texts.alwaysActive()}</Typography>
              <Switch checked={true} disabled color="primary" size="small" />
            </AppBox>
          </AppBox>
          <Typography sx={styles.categoryDescription}>{texts.necessary.description()}</Typography>
        </AppBox>

        <AppBox sx={styles.categoryBox}>
          <AppBox sx={styles.categoryHeader}>
            <Typography sx={styles.categoryTitle}>{texts.analytics.title()}</Typography>
            <Switch
              checked={localConsent.analytics}
              onChange={() => handleToggle('analytics')}
              color="primary"
              size="small"
            />
          </AppBox>
          <Typography sx={styles.categoryDescription}>{texts.analytics.description()}</Typography>
        </AppBox>

        <AppBox sx={styles.categoryBox}>
          <AppBox sx={styles.categoryHeader}>
            <Typography sx={styles.categoryTitle}>{texts.marketing.title()}</Typography>
            <Switch
              checked={localConsent.marketing}
              onChange={() => handleToggle('marketing')}
              color="primary"
              size="small"
            />
          </AppBox>
          <Typography sx={styles.categoryDescription}>{texts.marketing.description()}</Typography>
        </AppBox>

        <AppBox sx={styles.categoryBox}>
          <AppBox sx={styles.categoryHeader}>
            <Typography sx={styles.categoryTitle}>{texts.preferences.title()}</Typography>
            <Switch
              checked={localConsent.preferences}
              onChange={() => handleToggle('preferences')}
              color="primary"
              size="small"
            />
          </AppBox>
          <Typography sx={styles.categoryDescription}>{texts.preferences.description()}</Typography>
        </AppBox>

        <AppDivider />

        <AppBox sx={styles.buttonContainer}>
          <AppButton
            fontSize={12}
            variant="light"
            onClick={onClose}
            textValue={texts.cancel()}
          />
          <AppButton
            fontSize={12}
            variant="dark"
            onClick={handleAcceptAll}
            textValue={texts.acceptAll()}
          />
          <AppButton
            fontSize={12}
            variant="contained"
            onClick={handleSave}
            textValue={texts.saveSettings()}
          />
        </AppBox>
      </AppBox>
    </ModalWindow>
  )
})

const styles: Record<string, SxProps<Theme>> = {
  container: {
    flexDirection: 'column',
    gap: 10,
  },
  categoryBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  categoryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexShrink: 0,
  },
  categoryTitle: {
    fontSize: 14,
    fontWeight: 600,
  },
  categoryDescription: {
    fontSize: 12,
    lineHeight: 1.5,
    color: 'text.secondary',
  },
  alwaysActive: {
    fontSize: 12,
    color: 'secondary.main',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 5,
    justifyContent: 'flex-end',
  },
}
